import type { Option, Timescale } from "@tento-lona/sheets";
import { SheetViewConfig } from "@tento-lona/sheets";
import { SheetViewConfigRaw } from "@tento-lona/sheets/platform";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { PreferencesStorage } from "./preferences-storage.ts";
import { StoredPreference } from "./stored-preference.ts";
import { type Actor, type InvalidationSink, LayoutChanged } from "../invalidation.ts";
import type { RendererTz } from "@tento-lona/sheets";
import type { Uuid } from "@tento-core/uuid";
import type { SheetId } from "@tento-lona/sheets";

const DATE_UNITS: readonly Timescale[] = ["year", "month", "week", "day"];
const RENDERER_TZS: readonly RendererTz[] = ["default", "time-traveler"];

const DEFAULT_COUNTS: Map<Timescale, number> = new Map([
  ["year", 3],
  ["month", 3],
  ["week", 3],
  ["day", 7],
]);
const JOURNAL_SHEET_LOOKUP_KEY = ":journal";
const JOURNAL_DEFAULT_COUNT = 3;

function defaultCountsForSheet(
  sheetLookupKey: Option<string>,
): Map<Timescale, number> {
  const counts = new Map(DEFAULT_COUNTS);
  if (sheetLookupKey === JOURNAL_SHEET_LOOKUP_KEY) {
    counts.set("day", JOURNAL_DEFAULT_COUNT);
  }
  return counts;
}

function defaultConfigForSheet(
  timescale: Timescale,
  sheetLookupKey: Option<string>,
): SheetViewConfig {
  const resolvedTimescale =
    sheetLookupKey === JOURNAL_SHEET_LOOKUP_KEY ? "day" : timescale;
  const count = defaultCountsForSheet(sheetLookupKey).get(resolvedTimescale) ??
    1;
  if (resolvedTimescale === "day") {
    return { timescale: resolvedTimescale, count, tzType: "default" };
  }
  return { timescale: resolvedTimescale, count };
}

/** A sheet's resolved view preference: the view config and whether its column count was stated. */
export interface SheetViewPreferenceResolution {
  readonly config: SheetViewConfig;
  readonly countIsExplicit: boolean;
}

/**
 * One sheet's locally stored view settings (time scale, column counts,
 * day-view zone), `client.preferences.sheetView(sheetId)`. `peek()` reads
 * what is stored; `peekResolved`, `peekCounts` and `peekCount` read what it
 * resolves to for the sheet (its defaults where nothing is stored); and
 * `set(config, sheetLookupKey, actor)` is the one writer.
 */
export class SheetViewPreferences {
  readonly #storage: PreferencesStorage;
  readonly #sheetId: SheetId;
  readonly #invalidation: InvalidationSink;
  readonly #lifecycle: ClientLifecycle;

  /** `lifecycle`: the owning client's (a standalone face: `new LonaSdk.Client.Lifecycle()`). */
  constructor(storage: PreferencesStorage, sheetId: SheetId, invalidation: InvalidationSink, lifecycle: ClientLifecycle) {
    this.#storage = storage;
    this.#sheetId = sheetId;
    this.#invalidation = invalidation;
    this.#lifecycle = lifecycle;
  }

  static defaultConfig(timescale: Timescale): SheetViewConfig {
    return defaultConfigForSheet(timescale, null);
  }

  static defaultConfigForSheet(
    timescale: Timescale,
    sheetLookupKey: Option<string>,
  ): SheetViewConfig {
    return defaultConfigForSheet(timescale, sheetLookupKey);
  }

  static defaultCounts(): Map<Timescale, number> {
    return defaultCountsForSheet(null);
  }

  static defaultCountsForSheet(
    sheetLookupKey: Option<string>,
  ): Map<Timescale, number> {
    return defaultCountsForSheet(sheetLookupKey);
  }

  /** The stored key: `sheet/<id>/config`, the id's canonical text (`ss_…`). */
  #key(): string {
    return `sheet/${this.#sheetId.toString()}/config`;
  }

  /**
   * The key to read: the canonical one, or — while it holds nothing — the
   * UUID spelling a server sheet's settings were written under before the
   * view took a `SheetId` (`sheet/<uuid>/config`), so no stored setting is
   * lost; the next `set` writes the canonical key.
   */
  #readKey(): string {
    const key = this.#key();
    if (this.#storage.getItem(key) != null) return key;
    const uuid = this.#sheetId.toUuid();
    if (uuid == null) return key;
    const legacy = `sheet/${uuid}/config`;
    return this.#storage.getItem(legacy) != null ? legacy : key;
  }

  /**
   * The stored config, validated field by field; `null` when absent or when
   * its `timeScale` / `count` are unusable (logged). An unusable
   * `dayTzType` reads as `"default"`; unusable per-scale counts are dropped.
   */
  peek(): Option<SheetViewConfigRaw> {
    const key = this.#readKey();
    const stored = StoredPreference.record(key, StoredPreference.json(this.#storage, key));
    if (stored == null) return null;
    const timeScale = StoredPreference.oneOf(key, stored["timeScale"] ?? null, DATE_UNITS);
    const count = StoredPreference.finite(key, stored["count"] ?? null);
    if (timeScale == null || count == null) return null;
    const dayTzType =
      StoredPreference.oneOf(key, stored["dayTzType"] ?? null, RENDERER_TZS) ?? "default";
    const storedCounts = StoredPreference.record(key, stored["counts"] ?? null);
    if (storedCounts == null) return { timeScale, count, dayTzType, counts: {} };
    const counts: Partial<Record<Timescale, number>> = {};
    for (const [scale, value] of Object.entries(storedCounts)) {
      const unit = StoredPreference.oneOf(key, scale, DATE_UNITS);
      const scaleCount = StoredPreference.finite(key, value);
      if (unit != null && scaleCount != null) counts[unit] = scaleCount;
    }
    return { timeScale, count, dayTzType, counts };
  }

  /**
   * The view to open the sheet with: the stored config, else the sheet's
   * default at `defaultTimescale` (a `:journal` sheet opens on day);
   * `countIsExplicit` says which.
   */
  peekResolved(
    defaultTimescale: Timescale,
    sheetLookupKey: Option<string>,
  ): SheetViewPreferenceResolution {
    const raw = this.peek();
    if (!raw) {
      return {
        config: defaultConfigForSheet(defaultTimescale, sheetLookupKey),
        countIsExplicit: false,
      };
    }
    return {
      config: SheetViewConfigRaw.resolve(raw),
      countIsExplicit: true,
    };
  }

  /** The column count per time scale: the stored counts over the sheet's defaults. */
  peekCounts(
    sheetLookupKey: Option<string>,
  ): Map<Timescale, number> {
    const defaults = defaultCountsForSheet(sheetLookupKey);
    const raw = this.peek();
    if (!raw) return defaults;
    if (raw.counts) {
      for (const unit of DATE_UNITS) {
        const count = raw.counts[unit];
        if (count != null) defaults.set(unit, count);
      }
    }
    defaults.set(raw.timeScale, raw.count);
    return defaults;
  }

  /** `peekCounts(sheetLookupKey)`'s count at `timeScale` (`1` when it has none). */
  peekCount(
    timescale: Timescale,
    sheetLookupKey: Option<string>,
  ): number {
    return this.peekCounts(sheetLookupKey).get(timescale) ?? 1;
  }

  /**
   * Store `config` as the sheet's view, keeping the other time scales'
   * counts, and announce `LayoutChanged` stamped with `actor`, the writer.
   */
  set(config: SheetViewConfig, sheetLookupKey: Option<string>, actor: Actor): void {
    this.#lifecycle.assertLive("preferences.sheetView.set");
    const counts = this.peekCounts(sheetLookupKey);
    counts.set(config.timescale, config.count);
    const dayTzType =
      config.timescale === "day" ? (config.tzType ?? "default") : "default";
    const raw: SheetViewConfigRaw = {
      timeScale: config.timescale,
      count: config.count,
      dayTzType,
      counts: Object.fromEntries(counts),
    };
    this.#storage.setItem(this.#key(), JSON.stringify(raw));
    this.#invalidation.emit(new LayoutChanged(actor));
  }
}
